import React from 'react';
import PropTypes from 'prop-types';
import { Markup } from 'interweave';
import I18n from 'utils/I18n';
import Analytics from 'utils/analytics';
import Avatar from '@strava/ui/Avatar';
import Stat from '@strava/ui/Stat';
import Stats from '@strava/ui/Stats';

import ActivityIcon from 'components/ActivityIcon';

import ImageGrid from '../ImageGrid';
import styles from './styles.scss';
import emptyActivitiesImg from './images/img-empty.png';
import emptyActivitiesImg2x from './images/img-empty@2x.png';

// Good candidate for a shared Upsell component
const Upsell = () => (
  <div className={styles.upsellWrapper}>
    <div className={`${styles.upsell} media media-middle`}>
      <div className="media-body">
        <h4>{I18n.t('strava.athletes.show_public.mobile_cta')}</h4>
      </div>
      <div className="media-right">
        <a
          className={`btn btn-white ${styles.cta}`}
          href="/register"
          onClick={() =>
            Analytics.trackV2({
              category: 'athletes',
              page: 'athlete_profile',
              action: 'click',
              element: 'mobile_signup_button'
            })
          }
        >
          {I18n.t('strava.athletes.show_public.get_strava_cta')}
        </a>
      </div>
    </div>
  </div>
);

const ActivityStats = ({ activity }) => {
  const stats =
    activity.isManual || !activity.hasGps
      ? { time: activity.movingTime }
      : {
          distance: activity.distance,
          elevation: activity.elevation,
          time: activity.movingTime
        };

  return (
    <Stats listClassName={styles.listStats}>
      {Object.keys(stats).map((stat) => (
        <Stat
          label={I18n.t(`strava.athletes.show_public.${stat}`)}
          value={<Markup noWrap={true} content={stats[stat]} />}
          valueClassName={styles.statValue}
          key={stat}
        />
      ))}
    </Stats>
  );
};

ActivityStats.propTypes = {
  activity: PropTypes.shape().isRequired
};

const EmptyActivities = ({ athlete }) => (
  <>
    <img
      src={emptyActivitiesImg}
      alt=""
      srcSet={`${emptyActivitiesImg} 1x, ${emptyActivitiesImg2x} 2x`}
    />

    <p className={styles.empty}>
      <a href={athlete.followUrl}>
        {I18n.t('strava.athletes.show_public.activity_empty_state', {
          athlete_name: athlete.name
        })}
      </a>
    </p>

    <Upsell />
  </>
);

EmptyActivities.propTypes = {
  athlete: PropTypes.shape().isRequired
};

const RecentActivities = ({ activities, athlete }) => (
  <>
    <ol>
      {activities.map((activity) => (
        <li className={`card ${styles.card}`} key={activity.id}>
          <div className={`card-header ${styles.cardHeader}`}>
            <div className="media">
              <div className={`media-left ${styles.sideColumn}`}>
                <Avatar
                  name={athlete.name}
                  src={athlete.avatarUrl}
                  type="athlete"
                />
              </div>

              <div className="media-body">
                <time
                  className={styles.timestamp}
                  dateTime={activity.startDateLocal}
                >
                  {activity.startDateLocal}
                </time>
                <h4 className={styles.title}>{activity.name}</h4>
              </div>
            </div>

            <div className="media">
              <div className={`media-left ${styles.sideColumn}`}>
                <ActivityIcon
                  type={activity.detailedType}
                  size="small"
                  title={I18n.t(`strava.activities.types.${activity.type}`)}
                />
              </div>

              <div className="media-body">
                <ActivityStats activity={activity} />
              </div>
            </div>
          </div>
          {activity.images.length > 0 && (
            <div className={`card-body ${styles.cardBody}`}>
              <ImageGrid images={activity.images} />
            </div>
          )}
        </li>
      ))}
    </ol>
    <Upsell />
  </>
);

RecentActivities.propTypes = {
  activities: PropTypes.arrayOf(PropTypes.shape()).isRequired,
  athlete: PropTypes.shape().isRequired
};

const Activities = ({ activities, athlete }) => {
  const hasActivities =
    activities !== null && activities !== undefined && activities.length > 0;

  return hasActivities ? (
    <RecentActivities activities={activities} athlete={athlete} />
  ) : (
    <EmptyActivities athlete={athlete} />
  );
};

Activities.propTypes = {
  athlete: PropTypes.shape({
    name: PropTypes.string.isRequired,
    avatarUrl: PropTypes.string.isRequired,
    primarySport: PropTypes.string.isRequired,
    followUrl: PropTypes.string.isRequired
  }).isRequired,
  activities: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.number.isRequired,
      name: PropTypes.string.isRequired,
      type: PropTypes.string.isRequired,
      detailedType: PropTypes.string.isRequired,
      startDateLocal: PropTypes.string.isRequired,
      distance: PropTypes.string.isRequired,
      movingTime: PropTypes.string.isRequired,
      elevation: PropTypes.string.isRequired,
      images: PropTypes.arrayOf(PropTypes.shape())
    })
  )
};

Activities.defaultProps = {
  activities: []
};

export default Activities;
